import {
  ATOMIC_BOMB_JEWEL_ID,
  BLADE_CUT_JEWEL_ID,
  BLEEDING_JEWEL_ID,
  DAMAGE_REFLECTION_JEWEL_ID,
  JEWEL_LEVEL_MAX,
  jewelDefinition,
  MISSILE_JEWEL_ID,
  POISON_JEWEL_ID,
  REGENERATION_JEWEL_ID,
  SAW_JEWEL_ID,
} from '@kenocyber/shared';
import { BLEEDING_MAX_CHANCE_BPS } from './bleeding';
import { MISSILE_LIFETIME_MS } from './missile';
import { POISON_DURATION_MS, POISON_MAX_CHANCE_BPS, POISON_TICK_INTERVAL_MS } from './poison';
import {
  CYCLONE_MAX_CHANCE_BPS,
  PROC_CHANCE_DENOMINATOR,
  PROC_LEVEL_CAP,
  procChanceNumerator,
  procDamageMilli,
} from './proc-lottery';
import { REFLECTION_MAX_CHANCE_BPS } from './reflection';
import { sawBladeCountForLevel } from './saw';
import { VITAL_HEART_HEAL_SHARE_BPS, VITAL_HEART_MAX_CHANCE_BPS } from './vital-heart';
import { VOLLEY_PROC_REGISTRY } from './volley-lottery';

/**
 * O que uma joia FAZ, em palavras, derivado da mesma fonte que o combate usa.
 *
 * ## Porque isto é código e não texto na interface
 *
 * Porque um número escrito num `.tsx` ou num `.css` é uma segunda fonte de
 * verdade, e a segunda é a que fica errada. `+0,30%` numa string e `30` em
 * `JEWEL_DAMAGE_PER_LEVEL_BPS` são a mesma decisão em dois sítios; no dia em que
 * o coeficiente mudar, um deles muda.
 *
 * Aqui a frase é DERIVADA da constante. Mudar o coeficiente muda o que o jogador
 * lê, sem que ninguém tenha de se lembrar.
 *
 * ## Porque vive em `shared` e não na app web
 *
 * Porque é a mesma pergunta que o resolvedor de efeitos responde em números, e a
 * resposta tem de vir do mesmo catálogo. A camada de apresentação formata; ela
 * não decide quanto vale uma joia.
 */

/**
 * Apara um decimal fixo: `13,00` fica `13`; `4,30` fica `4,3`; `0,43` fica.
 *
 * A mesma gramática de {@link formatBps}, para as duas metades da mesma frase
 * não escreverem números de maneiras diferentes - uma chance de `4,33` ao lado
 * de um corte de `69,23` tem de se ler como o mesmo idioma.
 */
function trim(value: string): string {
  return value.replace(/,?0+$/, '').replace(/,$/, '');
}

/**
 * Basis points como percentagem, com o mínimo de casas que não perde exatidão.
 *
 * Inteiro sempre que possível - `900` bps é `9%`, não `9,00%` - e com casas só
 * quando elas dizem alguma coisa: `30` bps é `0,3%`.
 *
 * Vírgula decimal, porque a interface é `pt-BR`.
 */
function formatBps(bps: number): string {
  const whole = Math.trunc(bps / 100);
  const fraction = Math.abs(bps % 100);
  if (fraction === 0) {
    return `${whole}`;
  }
  const digits = fraction % 10 === 0 ? 1 : 2;
  return `${whole},${fraction.toString().padStart(2, '0').slice(0, digits)}`;
}

/* -------------------------------------------------------------------------- */
/*  As oito mecânicas de subsistema (gate V28G.W.1)                           */
/* -------------------------------------------------------------------------- */

/**
 * **Uma fração como percentagem `pt-BR`, com até duas casas.**
 *
 * As chances não são basis points inteiros: elas vivem sobre
 * {@link PROC_CHANCE_DENOMINATOR}, que é `BPS_ONE x 30`. O Ciclone ao nível 1
 * vale `1300 / 300 000` = 0,4333...% - um número que `formatBps` não sabe
 * escrever porque ele espera um inteiro.
 *
 * Duas casas e sem zeros finais, como o resto do ficheiro: `13`, `4,33`,
 * `0,43`.
 */
function formatRatioPercent(numerator: number, denominator: number): string {
  return trim(((numerator * 100) / denominator).toFixed(2).replace('.', ','));
}

/**
 * A chance desta joia, ao nível desta cópia, em palavras.
 *
 * `procChanceNumerator` é a MESMA função que a simulação usa para medir o
 * intervalo da joia no espaço de rolagem, e ela já corta o nível em
 * {@link PROC_LEVEL_CAP}. É por isso que um nível 31 lê aqui exatamente o que a
 * nave sorteia.
 */
function chancePercent(maxChanceBps: number, level: number): string {
  return formatRatioPercent(procChanceNumerator(maxChanceBps, level), PROC_CHANCE_DENOMINATOR);
}

/** O teto de chance que o registo de salva declara para esta joia. */
function volleyChanceBps(jewelId: string): number {
  const found = VOLLEY_PROC_REGISTRY.find((entry) => entry.jewelId === jewelId);
  if (found === undefined) {
    throw new Error(`${jewelId} is not registered as a volley proc`);
  }
  return found.maxChanceBps;
}

/**
 * Uma escala grande, para a divisão inteira de {@link procDamageMilli} não
 * perder as casas que a frase mostra. Não é um valor de jogo.
 */
const SHARE_PROBE_MILLI = 1_000_000_000n;

/**
 * **Quanto do dano-base o corte do Ciclone vale, em basis points.**
 *
 * Medido com a PRÓPRIA função que aplica o corte, e não com uma fórmula
 * reescrita: `procDamageMilli` recebe um dano-base conhecido e devolve o corte,
 * e a razão entre os dois é o que a frase mostra.
 *
 * O nível cancela dentro dela - o orçamento é `chance x dano` e é constante -
 * portanto isto é o mesmo número em qualquer nível. Passá-lo na mesma é o que
 * mantém a frase certa se o orçamento algum dia deixar de o ser.
 */
function cycloneCutShareBps(level: number): number {
  const cut = procDamageMilli(SHARE_PROBE_MILLI, CYCLONE_MAX_CHANCE_BPS, BigInt(level));
  return Number((cut * 10_000n) / SHARE_PROBE_MILLI);
}

/** Milissegundos em segundos, para uma duração se ler numa frase. */
function seconds(ms: number): string {
  return trim((ms / 1000).toFixed(2).replace('.', ','));
}

/**
 * A cadência de uma marca de veneno, em palavras.
 *
 * Derivada do intervalo, e não escrita: um intervalo de mil milissegundos é
 * "uma vez por segundo", e qualquer outro tem de se dizer em segundos. Sem este
 * ramo, mudar o intervalo deixaria a frase a prometer uma cadência que a marca
 * já não tem.
 */
function tickCadence(intervalMs: number): string {
  return intervalMs === 1_000
    ? 'uma vez por segundo'
    : `uma vez a cada ${seconds(intervalMs)} segundos`;
}

/* -------------------------------------------------------------------------- */
/*  As oito frases, e de onde vem cada número                                 */
/* -------------------------------------------------------------------------- */

/**
 * As joias cuja mecânica vive num subsistema, e não num coeficiente.
 *
 * Um `Set` e não uma cadeia de `if`: `jewelInstanceEffectText` precisa de saber
 * se a joia é especializada ANTES de validar o nível, e perguntá-lo duas vezes
 * a uma cadeia seria duas cadeias a envelhecer.
 */
const SPECIALISED_MECHANICS: ReadonlySet<string> = new Set([
  BLADE_CUT_JEWEL_ID,
  ATOMIC_BOMB_JEWEL_ID,
  MISSILE_JEWEL_ID,
  POISON_JEWEL_ID,
  BLEEDING_JEWEL_ID,
  DAMAGE_REFLECTION_JEWEL_ID,
  SAW_JEWEL_ID,
  REGENERATION_JEWEL_ID,
]);

/**
 * **O que uma das oito faz, ao nível desta cópia.**
 *
 * Cada número sai da constante ou da função que a MECÂNICA usa - a chance de
 * `procChanceNumerator`, o corte de `procDamageMilli`, a duração do míssil de
 * `MISSILE_LIFETIME_MS`, as serras de `sawBladeCountForLevel`. Nenhum valor de
 * equilíbrio é reescrito aqui, e é isso que faz mover a mecânica mover a frase.
 */
function specialisedMechanicText(definitionId: string, level: number): string | null {
  switch (definitionId) {
    case BLADE_CUT_JEWEL_ID:
      /*
       * O corte é uma FRAÇÃO do dano-base, medida com a própria função que o
       * aplica. O nível cancela lá dentro - o orçamento é `chance x dano` - por
       * isso a percentagem do corte é a mesma em qualquer nível, e só a chance
       * cresce.
       */
      return `Acertos diretos têm ${chancePercent(CYCLONE_MAX_CHANCE_BPS, level)}% de chance de causar um corte adicional de ${formatBps(cycloneCutShareBps(level))}% do dano-base.`;
    case ATOMIC_BOMB_JEWEL_ID:
      /*
       * «No PvP» explícito, e não uma promessa geral.
       *
       * O empurrão da explosão só existe no duelo, e esta carta é do
       * INVENTÁRIO - ela não sabe em que modo o jogador vai entrar. Prometer o
       * empurrão sem a condição seria prometer à caça uma coisa que ela não faz.
       */
      return `Cada salva tem ${chancePercent(volleyChanceBps(ATOMIC_BOMB_JEWEL_ID), level)}% de chance de lançar uma Bomba Atômica, causando dano em área. No PvP, a explosão também empurra inimigos.`;
    case MISSILE_JEWEL_ID:
      return `Cada salva tem ${chancePercent(volleyChanceBps(MISSILE_JEWEL_ID), level)}% de chance de lançar um míssil teleguiado por até ${seconds(MISSILE_LIFETIME_MS)} segundos, causando dano a um único inimigo.`;
    case POISON_JEWEL_ID:
      /*
       * Sem uma palavra sobre limite de pilhas: as marcas são independentes e
       * ilimitadas, e escrever um teto que não existe seria pior do que a carta
       * muda que esta frase veio fechar.
       */
      return `Acertos diretos têm ${chancePercent(POISON_MAX_CHANCE_BPS, level)}% de chance de aplicar Veneno. Cada marca dura ${seconds(POISON_DURATION_MS)} segundos e causa dano ${tickCadence(POISON_TICK_INTERVAL_MS)}.`;
    case BLEEDING_JEWEL_ID:
      /*
       * **Nunca "dano por segundo".** As marcas não doem enquanto estão postas:
       * elas acumulam, e é a DETONAÇÃO que cobra. Descrevê-lo como periódico
       * ensinaria o jogador a esperar dano que não vem.
       */
      return `Acertos diretos têm ${chancePercent(BLEEDING_MAX_CHANCE_BPS, level)}% de chance de aplicar uma marca. Detone todas para causar dano proporcional à quantidade acumulada.`;
    case DAMAGE_REFLECTION_JEWEL_ID:
      /*
       * «Somente PvP» na frase, e a etiqueta `SOMENTE PVP` continua ao lado do
       * nome. Duas vezes de propósito: a etiqueta é uma marca visual, e esta é a
       * oração que um leitor de ecrã lê seguida do resto do efeito.
       */
      return `Ao receber um ataque, tem ${chancePercent(REFLECTION_MAX_CHANCE_BPS, level)}% de chance de anulá-lo e devolvê-lo ao atacante. Somente PvP.`;
    case SAW_JEWEL_ID: {
      /*
       * **Sem uma palavra de chance**: a serra é passiva e está sempre lá.
       *
       * O único número que a carta mostra é a QUANTIDADE, porque é o único que
       * se lê exato de uma função (`sawBladeCountForLevel`). O dano por contacto
       * é uma razão com carry entre lâminas, velocidade e intervalo - uma
       * percentagem ali seria uma simplificação que o código não sustenta.
       */
      const blades = sawBladeCountForLevel(level);
      const noun = blades === 1 ? 'serra orbitando' : 'serras orbitando';
      return `Mantém ${blades} ${noun} a nave. Os níveis aumentam a quantidade, o dano e a velocidade das serras, que podem ativar joias compatíveis.`;
    }
    case REGENERATION_JEWEL_ID:
      /*
       * A magnitude é `VITAL_HEART_HEAL_SHARE_BPS` e não um 150 escrito: mover a
       * cura move a frase. A chance é a DESTE nível, e não os 6 % do trigésimo.
       */
      return `Cada salva tem ${chancePercent(VITAL_HEART_MAX_CHANCE_BPS, level)}% de chance de curar o aliado ferido mais próximo em ${formatBps(Number(VITAL_HEART_HEAL_SHARE_BPS))}% do dano-base. Sem aliado ferido, cura a própria nave.`;
    default:
      return null;
  }
}

/**
 * O efeito por NÍVEL, para o catálogo. `null` quando a joia não concede nada.
 *
 * "dano causado" e nunca "dano recebido": as duas coisas existem no contrato -
 * a Égide Esmeralda reduz dano recebido - e trocá-las seria dizer ao jogador o
 * contrário do que a peça faz.
 */
export function jewelPerLevelEffectText(definitionId: string): string | null {
  const effects = jewelDefinition(definitionId)?.effects;
  const moveSpeedPerLevel = effects?.moveSpeedPerLevelSumBps;
  if (moveSpeedPerLevel !== undefined) {
    /*
     * **O texto do Passo Alado** (gate V28G.T), e os dois números dele saem das
     * constantes.
     *
     * "velocidade de movimento da nave" e nunca "velocidade": as duas coisas
     * existem no contrato - o Pulso do Dragão acelera a CADÊNCIA - e trocá-las
     * seria dizer ao jogador o contrário do que a peça faz.
     */
    return `Aumenta em ${formatBps(moveSpeedPerLevel)}% por nível a velocidade de movimento da nave. Máximo: ${formatBps(effects?.moveSpeedMaxBps ?? 0)}%.`;
  }
  const maxHealthPerLevel = effects?.maxHealthPerLevelSumBps;
  if (maxHealthPerLevel !== undefined) {
    /*
     * **O texto aprovado do Coração do Titã** (gate V28G.R), e os dois números
     * dele saem das constantes.
     *
     * "vida máxima total formada pela nave e por seus bônus planos" é a
     * composição escrita em palavras: toda a vida plana primeiro, e esta
     * percentagem sobre ela. Escrever `2 %` e `60 %` à mão aqui - ou num `.tsx`,
     * ou num `.css` - seria a segunda casa da mesma decisão, e a segunda é a que
     * fica errada.
     */
    return `Aumenta em ${formatBps(maxHealthPerLevel)}% por nível a vida máxima total formada pela nave e por seus bônus planos. Máximo: ${formatBps(effects?.maxHealthMaxBps ?? 0)}%.`;
  }
  const attackSpeedPerLevel = effects?.attackSpeedPerLevelSumBps;
  if (attackSpeedPerLevel !== undefined) {
    /*
     * **O texto aprovado do Pulso do Dragão** (gate V28G.V), e os três números
     * dele saem das constantes.
     *
     * "velocidade de ataque" e nunca "velocidade": as duas coisas existem no
     * contrato - o Passo Alado acelera o MOVIMENTO - e trocá-las seria dizer ao
     * jogador o contrário do que a peça faz.
     *
     * ## Porque o teto é `coef x nível máximo` e não um campo do catálogo
     *
     * Porque esta joia não declara `attackSpeedMaxBps`, e não declarar não é um
     * esquecimento: o teto dela **é** o nível. `resolveJewelEffects` corta o
     * `levelSum` em {@link JEWEL_LEVEL_MAX} antes de multiplicar, e não há
     * segundo corte a seguir - portanto o máximo que a cadência alguma vez vê é
     * exatamente este produto.
     *
     * Escrever `9 %` à mão aqui seria a segunda casa da mesma decisão, e a
     * segunda é a que fica errada.
     */
    return `Aumenta a velocidade de ataque em ${formatBps(attackSpeedPerLevel)}% por nível, até ${formatBps(ceilingBps(attackSpeedPerLevel, NO_DECLARED_CEILING))}% no nível ${JEWEL_LEVEL_MAX}.`;
  }
  const lifestealPerLevel = effects?.lifestealPerLevelSumBps;
  if (lifestealPerLevel !== undefined) {
    /*
     * **O texto aprovado do Orbe Vampírico** (gate V28G.V), e os números dele
     * saem das constantes.
     *
     * ## «Ativação: 100%», e porque a frase o diz em voz alta
     *
     * Porque esta é a única joia com magnitude por nível que **não sorteia**, e
     * o jogador não tem como saber isso ao olhar para ela. Todas as vizinhas com
     * um número por nível ou têm uma chance, ou são passivas contínuas - e o
     * silêncio sobre a ativação lê-se como "há uma chance que ninguém escreveu".
     *
     * Não há linguagem de chance nesta frase de propósito: nem "pode", nem "às
     * vezes", nem "probabilidade". Todo golpe elegível cura.
     *
     * ## «do dano elegível efetivamente causado», e as duas palavras que contam
     *
     * **Elegível** porque a origem decide: a tabela autoritativa de origens
     * exclui golpes que não alimentam o sifão, e prometer "todo o dano" seria
     * prometer origens que ela recusa.
     *
     * **Efetivamente causado** porque a cura sai de `appliedMilli` - o dano
     * DEPOIS da redução do alvo e depois do corte de overkill - e não da
     * magnitude que o atacante lançou. Um golpe de 40 num alvo com 3 de vida
     * cura sobre 3, e dizer "dano causado" prometeria 40.
     *
     * ## E o teto é o do NÍVEL, não o `lifestealMaxBps` do catálogo
     *
     * O catálogo declara 50 %, e esse número nunca é atingido: o `levelSum` é
     * cortado em {@link JEWEL_LEVEL_MAX} antes de multiplicar, portanto o
     * máximo real é `coef x 30` = 9 %. O teto declarado fica onde está - é uma
     * decisão de equilíbrio que o resolvedor lê - mas quem manda na frase é o
     * limite que morde primeiro, que é este.
     */
    return `Cura ${formatBps(lifestealPerLevel)}% por nível do dano elegível efetivamente causado. Ativação: 100%. Máximo de ${formatBps(ceilingBps(lifestealPerLevel, effects?.lifestealMaxBps))}% no nível ${JEWEL_LEVEL_MAX}.`;
  }
  const reductionPerLevel = effects?.damageReductionPerLevelSumBps;
  if (reductionPerLevel !== undefined) {
    /*
     * **O texto aprovado da Égide Esmeralda** (gate V28G.Q), e os dois números
     * dele saem das constantes.
     *
     * Escrever `2 %` e `60 %` à mão aqui - ou num `.tsx`, ou num `.css` - seria
     * a segunda casa da mesma decisão, e a segunda é a que fica errada. O
     * coeficiente muda, e a frase muda com ele.
     *
     * O teto vem de `damageReductionMaxBps` e não de `coef x 30`: os dois dão
     * 60 % hoje, e só o primeiro continua a dizer a verdade no dia em que um
     * deles se mover.
     */
    return `Reduz em ${formatBps(reductionPerLevel)}% por nível todo dano hostil recebido. Máximo: ${formatBps(effects?.damageReductionMaxBps ?? 0)}%.`;
  }
  const perLevel = effects?.damagePerLevelSumBps;
  if (perLevel === undefined) {
    return null;
  }
  return `+${formatBps(perLevel)}% de dano causado por nível`;
}

/**
 * O efeito desta CÓPIA, ao nível que ela tem. `null` quando não concede nada.
 *
 * ## Porque o nível entra aqui e não numa multiplicação da interface
 *
 * Porque a lei é `coef x levelSum`, e `levelSum` de uma cópia é o nível dela.
 * Uma interface que multiplicasse por conta própria seria uma terceira cópia da
 * fórmula.
 *
 * ## O que esta frase deliberadamente NÃO promete
 *
 * Nada sobre subir de nível. `ITEM_LEVEL_CAPS.jewel` é `null` - nível 1 apenas -
 * e prometer um upgrade que o sistema recusa seria vender uma coisa que não
 * existe. A frase por nível existe para o catálogo explicar a regra; esta
 * existe para a cópia dizer o que faz hoje.
 */
export function jewelInstanceEffectText(definitionId: string, level: number): string | null {
  const effects = jewelDefinition(definitionId)?.effects;
  const reductionPerLevel = effects?.damageReductionPerLevelSumBps;
  const maxHealthPerLevel = effects?.maxHealthPerLevelSumBps;
  const moveSpeedPerLevel = effects?.moveSpeedPerLevelSumBps;
  const attackSpeedPerLevel = effects?.attackSpeedPerLevelSumBps;
  const lifestealPerLevel = effects?.lifestealPerLevelSumBps;
  const perLevel = effects?.damagePerLevelSumBps;
  const hasCoefficient =
    reductionPerLevel !== undefined ||
    maxHealthPerLevel !== undefined ||
    moveSpeedPerLevel !== undefined ||
    attackSpeedPerLevel !== undefined ||
    lifestealPerLevel !== undefined ||
    perLevel !== undefined;
  /*
   * **As oito de subsistema** (gate V28G.W.1).
   *
   * Elas não declaram coeficiente nenhum, portanto caíam no `return null` que
   * fechava esta função - e a carta ficava sem explicação para joias que
   * funcionam. A mecânica delas está registada nos módulos ao lado, e é de lá
   * que cada número da frase vem.
   */
  const specialised = SPECIALISED_MECHANICS.has(definitionId);
  if (!hasCoefficient && !specialised) {
    /*
     * Só a soberana chega aqui. Ela não ocupa socket, não tem nível e não tem
     * mecânica de socket - e uma frase sobre um nível que ela não tem seria
     * inventada.
     */
    return null;
  }
  /*
   * A validação do NÍVEL vale agora para as catorze, e não só para as seis.
   *
   * Ela estava depois da guarda dos coeficientes, portanto uma das oito com um
   * nível zero teria devolvido texto em vez de recusar. O corte do teto não
   * autoriza dados corrompidos: zero, negativo e fracionário atiram aqui como
   * atiram no armazenamento, no socket e no perfil.
   */
  if (!Number.isSafeInteger(level) || level < 1) {
    throw new RangeError(`a jewel level is a whole number of at least 1, got ${level}`);
  }
  if (specialised) {
    return specialisedMechanicText(definitionId, level);
  }
  if (moveSpeedPerLevel !== undefined) {
    /*
     * O valor DESTA cópia, pela mesma lei que o movimento aplica:
     * `coef x nível`, cortado pelo teto declarado. O teto é o mesmo campo que o
     * resolvedor lê, e é por isso que um nível 31 leria aqui os mesmos 39 % que a
     * nave anda.
     */
    return `+${formatBps(cappedBps(moveSpeedPerLevel, effects?.moveSpeedMaxBps ?? 0, level))}% de velocidade de movimento`;
  }
  if (maxHealthPerLevel !== undefined) {
    /*
     * O valor DESTA cópia, pela mesma lei que o combate aplica: `coef x nível`,
     * cortado pelo teto declarado. O teto é o mesmo campo que o resolvedor lê, e
     * é por isso que um nível 31 leria aqui os mesmos 60 % que a vida mostra.
     */
    return `+${formatBps(cappedBps(maxHealthPerLevel, effects?.maxHealthMaxBps ?? 0, level))}% de vida máxima`;
  }
  if (attackSpeedPerLevel !== undefined) {
    /*
     * O valor DESTA cópia, pela mesma lei que o combate aplica (gate V28G.V).
     *
     * Sem teto declarado: o teto desta joia É o nível, e {@link cappedBps} corta
     * o nível antes de multiplicar - exatamente como `resolveJewelEffects` corta
     * o `levelSum`. É por isso que um nível 31 lê aqui os mesmos 9 % que a
     * cadência mostra.
     */
    return `+${formatBps(cappedBps(attackSpeedPerLevel, NO_DECLARED_CEILING, level))}% de velocidade de ataque`;
  }
  if (lifestealPerLevel !== undefined) {
    /*
     * O valor DESTA cópia, e a ativação outra vez (gate V28G.V).
     *
     * A ativação repete-se aqui de propósito. Esta é a frase que aparece na
     * CARTA de uma cópia - o sítio onde o jogador decide se a encaixa - e o
     * catálogo pode não estar aberto ao lado. Omiti-la deixaria a única joia com
     * magnitude por nível que não sorteia a parecer-se com as que sorteiam.
     */
    return `Cura ${formatBps(cappedBps(lifestealPerLevel, effects?.lifestealMaxBps, level))}% do dano elegível efetivamente causado. Ativação: 100%.`;
  }
  if (reductionPerLevel !== undefined) {
    /*
     * O valor DESTA cópia, pela mesma lei que o combate aplica: `coef x nível`,
     * cortado pelo teto declarado. Nunca uma multiplicação escrita na interface
     * e nunca um número escrito à mão - o teto é o mesmo campo que o resolvedor
     * lê, e é por isso que um nível 31 leria aqui os mesmos 60 % que o dano
     * recebido mostra.
     */
    return `Reduz ${formatBps(cappedBps(reductionPerLevel, effects?.damageReductionMaxBps ?? 0, level))}% de todo dano hostil recebido`;
  }
  /*
   * E o dano pelo MESMO corte que as outras cinco (gate V28G.V).
   *
   * Ele era a única grandeza a multiplicar o nível cru, e por isso um nível 31
   * lia `+9,3 %` numa carta enquanto a nave batia com 9 %. Não havia teto
   * declarado que o apanhasse, e não é preciso nenhum: o teto é o nível.
   */
  return `+${formatBps(cappedBps(perLevel ?? 0, NO_DECLARED_CEILING, level))}% de dano causado`;
}

/**
 * O valor desta cópia em basis points, cortado pelo teto que o catálogo declara.
 *
 * A mesma lei que `resolveJewelEffects` aplica - `min(coef x nível, teto)` - na
 * unidade que a interface formata. Uma função para TODAS as passivas com teto, e
 * não uma por joia: a lei é a mesma, e uma cópia por peça seria uma cópia a
 * divergir.
 */
/**
 * **Esta grandeza não declara teto, e não declarar é a decisão.**
 *
 * O Pulso do Dragão e a joia de dano não têm campo `MaxBps` no catálogo porque
 * o teto delas É o nível: `resolveJewelEffects` corta o `levelSum` em
 * {@link JEWEL_LEVEL_MAX} e não há segundo corte a seguir.
 *
 * Um nome e não um `undefined` solto em duas chamadas: escrito assim, quem lê a
 * chamada vê uma afirmação em vez de um argumento em falta.
 */
const NO_DECLARED_CEILING = undefined;

function cappedBps(perLevelBps: number, maxBps: number | undefined, level: number): number {
  /*
   * **O NÍVEL primeiro, e só depois o teto** (gate V28G.V).
   *
   * É a ordem que `resolveJewelEffects` usa: ele corta o `levelSum` em
   * {@link JEWEL_LEVEL_MAX} e só então multiplica. Multiplicar primeiro e
   * confiar no teto funcionava por coincidência aritmética - a vida, a redução e
   * o movimento declaram tetos que valem exatamente `coef x 30` - e falhava em
   * quem não tem essa coincidência: o Orbe declara 50 % e o nível 30 dá 9 %, e
   * sem este corte um nível 31 leria 9,3 %.
   *
   * O teto declarado continua a ser aplicado a seguir, porque ele é uma decisão
   * de equilíbrio à parte e pode um dia morder antes do nível.
   */
  const granted = perLevelBps * (level < JEWEL_LEVEL_MAX ? level : JEWEL_LEVEL_MAX);
  if (maxBps === undefined) {
    return granted;
  }
  return granted < maxBps ? granted : maxBps;
}

/**
 * O teto REAL de uma grandeza com coeficiente por nível, em basis points.
 *
 * O menor entre `coef x nível máximo` e o teto que o catálogo declara. Os dois
 * existem e são independentes: o primeiro é uma consequência do nível, o segundo
 * é uma decisão de equilíbrio - e a frase tem de mostrar o que morde primeiro.
 */
function ceilingBps(perLevelBps: number, maxBps: number | undefined): number {
  return cappedBps(perLevelBps, maxBps, JEWEL_LEVEL_MAX);
}

/** O coeficiente cru, para quem precisa do número e não da frase. */
export function jewelDamagePerLevelBps(definitionId: string): number | null {
  return jewelDefinition(definitionId)?.effects.damagePerLevelSumBps ?? null;
}

/** Exportado para uma prova poder comparar a frase com a constante. */
